import type { ProductInfoData } from "@/components/ui/product-info-modal"
import type { Availability } from "@/lib/availability"
import { blobMediaUrl } from "@/lib/blob-url"

/**
 * Austria sells the three MRI packages and nothing else. Every standalone test
 * behind these add-ons is already `availability: { "at-de": false }` in
 * `data/tests.ts`, so the add-on cards have to say the same thing — otherwise
 * /at/de/pricing advertises products whose detail pages 404. The two add-on
 * grids (`ImagingAddonsSection`, `BloodAddonsSection`) both read this and drop
 * to nothing, which is what removes the sections from the Austrian page.
 * Drop the line from an entry once Austria actually sells that add-on.
 */
const NOT_SOLD_IN_AUSTRIA: Availability = { "at-de": false }

/** Switzerland only — the blood add-ons the booking flow sells in CH alone. */
const CH_ONLY: Availability = {
  "nl-nl": false,
  "nl-en": false,
  "de-de": false,
  "de-en": false,
  "at-de": false,
}

export const MRI_PACKAGE_INFO: Record<string, ProductInfoData> = {
  Core: {
    nameKey: "piNameCore",
    image: blobMediaUrl("/images/homepage/service-mri-machine.jpeg"),
    durationKey: "~60 min",
    radiationKey: "piNoRadiation",
    contrastDyeKey: "piNoContrastDye",
    priceKey: "core",
    infoKeys: {
      healthAreas: "piCoreInfoHealthAreas",
      timeNeeded: "piCoreInfoTimeNeeded",
      radiation: "piNoneAtAll",
      contrastDye: "piNoneAtAllContrast",
      preparation: "piFast8Hours",
      forWhom: "piAdults18to80",
    },
    descKey: "infoCoreDesc",
    benefitKeys: ["piCoreBenefit1", "piCoreBenefit2", "piCoreBenefit3"],
    audienceKeys: ["piCoreAudience1", "piCoreAudience2", "piCoreAudience3"],
    whatYouGetKeys: [
      "piInsightsByPhysicians",
      "piDetailedReport",
      "piPersonalActionPlan",
      "piAddTestsAnytime",
    ],
    scanAreaKeys: [
      "piAreaBrain",
      "piAreaSpine",
      "piAreaNeck",
      "piAreaChest",
      "piAreaAbdomen",
      "piAreaPelvis",
    ],
  },
  Advanced: {
    nameKey: "piNameAdvanced",
    cmsPackageId: "advanced",
    image: blobMediaUrl("/images/homepage/service-mri-machine.jpeg"),
    durationKey: "~75 min",
    radiationKey: "piNoRadiation",
    contrastDyeKey: "piNoContrastDye",
    priceKey: "advanced",
    infoKeys: {
      healthAreas: "piAdvancedInfoHealthAreas",
      timeNeeded: "piAdvancedInfoTimeNeeded",
      radiation: "piNoneAtAll",
      contrastDye: "piNoneAtAllContrast",
      preparation: "piFast8Hours",
      forWhom: "piAdults18to80",
    },
    descKey: "infoAdvancedDesc",
    benefitKeys: [
      "piAdvancedBenefit1",
      "piAdvancedBenefit2",
      "piAdvancedBenefit3",
    ],
    audienceKeys: [
      "piAdvancedAudience1",
      "piAdvancedAudience2",
      "piAdvancedAudience3",
    ],
    whatYouGetKeys: [
      "piInsightsByPhysicians",
      "piDetailedReport",
      "piPersonalActionPlan",
      "piAddTestsAnytime",
    ],
    scanAreaKeys: [
      "piAreaBrain",
      "piAreaSpine",
      "piAreaNeck",
      "piAreaChest",
      "piAreaAbdomen",
      "piAreaPelvis",
      "piAreaProstateOvaries",
      "piAreaBodyComposition",
      "piAreaSpineMuscles",
    ],
  },
  Pro: {
    nameKey: "piNamePro",
    cmsPackageId: "pro",
    image: blobMediaUrl("/images/homepage/service-mri-machine.jpeg"),
    durationKey: "~90 min",
    radiationKey: "piNoRadiation",
    contrastDyeKey: "piNoContrastDye",
    priceKey: "pro",
    infoKeys: {
      healthAreas: "piProInfoHealthAreas",
      timeNeeded: "piProInfoTimeNeeded",
      radiation: "piNoneAtAll",
      contrastDye: "piNoneAtAllContrast",
      preparation: "piFast8Hours",
      forWhom: "piAdults18to80",
    },
    descKey: "infoProDesc",
    benefitKeys: ["piProBenefit1", "piProBenefit2", "piProBenefit3"],
    audienceKeys: ["piProAudience1", "piProAudience2", "piProAudience3"],
    whatYouGetKeys: [
      "piInsightsByPhysicians",
      "piDetailedReport",
      "piPersonalActionPlan",
      "piAddTestsAnytime",
    ],
    scanAreaKeys: [
      "piAreaBrain",
      "piAreaBrainVolumetry",
      "piAreaSpine",
      "piAreaNeck",
      "piAreaChest",
      "piAreaAbdomen",
      "piAreaPelvis",
      "piAreaProstateOvaries",
      "piAreaHips",
      "piAreaKnees",
      "piAreaBodyComposition",
      "piAreaSpineMuscles",
    ],
  },
}

export const ADDON_INFO: Record<string, ProductInfoData> = {
  addonBodyComposition: {
    availability: NOT_SOLD_IN_AUSTRIA,
    nameKey: "addonBodyComposition",
    image: blobMediaUrl("/images/addons/body-composition.jpg"),
    durationKey: "piIncludedInMri",
    radiationKey: "piNoRadiation",
    priceKey: "addonBodyComposition",
    descKey: "infoBodyCompDesc",
    benefitKeys: [
      "piAddonBodyCompositionBenefit1",
      "piAddonBodyCompositionBenefit2",
      "piAddonBodyCompositionBenefit3",
    ],
    audienceKeys: [
      "piAddonBodyCompositionAudience1",
      "piAddonBodyCompositionAudience2",
      "piAddonBodyCompositionAudience3",
    ],
    whatYouGetKeys: [
      "piInsightsByPhysicians",
      "piDetailedReport",
      "piPersonalActionPlan",
      "piAddTestsAnytime",
    ],
  },
  addonGenderHealth: {
    availability: NOT_SOLD_IN_AUSTRIA,
    nameKey: "addonGenderHealth",
    image: blobMediaUrl("/images/addons/gender-health.jpg"),
    durationKey: "piIncludedInMri",
    radiationKey: "piNoRadiation",
    priceKey: "addonGenderHealth",
    descKey: "infoProstateOvariesDesc",
    benefitKeys: [
      "piAddonGenderHealthBenefit1",
      "piAddonGenderHealthBenefit2",
      "piAddonGenderHealthBenefit3",
    ],
    audienceKeys: [
      "piAddonGenderHealthAudience1",
      "piAddonGenderHealthAudience2",
      "piAddonGenderHealthAudience3",
    ],
    whatYouGetKeys: [
      "piInsightsByPhysicians",
      "piDetailedReport",
      "piPersonalActionPlan",
      "piAddTestsAnytime",
    ],
  },
  addonHeartCT: {
    availability: NOT_SOLD_IN_AUSTRIA,
    nameKey: "addonHeartCT",
    image: blobMediaUrl("/images/addons/heart-ct.jpg"),
    durationKey: "10 min",
    radiationKey: "piLowDoseRadiation",
    priceKey: "addonHeartCT",
    infoKeys: {
      healthAreas: "piAddonHeartCTInfoHealthAreas",
      timeNeeded: "piAddonHeartCTInfoTimeNeeded",
      radiation: "piAddonHeartCTInfoRadiation",
      contrastDye: "piNone",
      preparation: "piAddonHeartCTInfoPreparation",
      forWhom: "piAddonHeartCTInfoForWhom",
    },
    descKey: "infoCalciumDesc",
    benefitKeys: [
      "piAddonHeartCTBenefit1",
      "piAddonHeartCTBenefit2",
      "piAddonHeartCTBenefit3",
    ],
    audienceKeys: [
      "piAddonHeartCTAudience1",
      "piAddonHeartCTAudience2",
      "piAddonHeartCTAudience3",
    ],
    whatYouGetKeys: [
      "piInsightsByPhysicians",
      "piDetailedReport",
      "piPersonalActionPlan",
      "piAddTestsAnytime",
    ],
  },
  addonMammaMRI: {
    availability: NOT_SOLD_IN_AUSTRIA,
    nameKey: "addonMammaMRI",
    image: blobMediaUrl("/images/addons/mamma-mri.jpg"),
    durationKey: "20 min",
    radiationKey: "piNoRadiation",
    contrastDyeKey: "piContrastDyeRequired",
    priceKey: "addonMammaMRI",
    infoKeys: {
      healthAreas: "piAddonMammaMRIInfoHealthAreas",
      timeNeeded: "piAddonMammaMRIInfoTimeNeeded",
      radiation: "piNoneAtAll",
      contrastDye: "piAddonMammaMRIInfoContrastDye",
      preparation: "piAddonMammaMRIInfoPreparation",
      forWhom: "piAddonMammaMRIInfoForWhom",
    },
    descKey: "infoMammaDesc",
    benefitKeys: [
      "piAddonMammaMRIBenefit1",
      "piAddonMammaMRIBenefit2",
      "piAddonMammaMRIBenefit3",
    ],
    audienceKeys: ["piAddonMammaMRIAudience1", "piAddonMammaMRIAudience2"],
    whatYouGetKeys: [
      "piInsightsByPhysicians",
      "piDetailedReport",
      "piPersonalActionPlan",
      "piAddTestsAnytime",
    ],
  },
  addonDEXA: {
    availability: NOT_SOLD_IN_AUSTRIA,
    nameKey: "addonDEXA",
    image: blobMediaUrl("/images/addons/dexa.jpg"),
    durationKey: "15 min",
    radiationKey: "piVeryLowDoseRadiation",
    priceKey: "addonDEXA",
    descKey: "infoDexaDesc",
    benefitKeys: [
      "piAddonDEXABenefit1",
      "piAddonDEXABenefit2",
      "piAddonDEXABenefit3",
    ],
    audienceKeys: [
      "piAddonDEXAAudience1",
      "piAddonDEXAAudience2",
      "piAddonDEXAAudience3",
    ],
    whatYouGetKeys: [
      "piInsightsByPhysicians",
      "piDetailedReport",
      "piPersonalActionPlan",
      "piAddTestsAnytime",
    ],
  },
  advancedBloodTest: {
    availability: NOT_SOLD_IN_AUSTRIA,
    nameKey: "advancedBloodTestTitle",
    cmsPackageId: "blood",
    image: blobMediaUrl("/images/addons/multi-vial-blue.png"),
    durationKey: "15 min",
    radiationKey: "piNoRadiation",
    contrastDyeKey: "piNoContrastDye",
    priceKey: "blood",
    infoKeys: {
      healthAreas: "piAdvancedBloodTestInfoHealthAreas",
      timeNeeded: "piAdvancedBloodTestInfoTimeNeeded",
      radiation: "piNoneAtAll",
      contrastDye: "piNoneAtAllContrast",
      preparation: "piFast8to12Hours",
      forWhom: "piAdults18plus",
    },
    descKey: "infoBloodPanelDesc",
    benefitKeys: ["piAdvancedBloodTestBenefit1", "piAdvancedBloodTestBenefit2"],
    audienceKeys: [
      "piAdvancedBloodTestAudience1",
      "piAdvancedBloodTestAudience2",
      "piAdvancedBloodTestAudience3",
    ],
    whatYouGetKeys: [
      "piInsightsByPhysicians",
      "piDetailedReport",
      "piPersonalActionPlan",
      "piAddTestsMriAnytime",
    ],
  },
  addonBVitamins: {
    availability: NOT_SOLD_IN_AUSTRIA,
    nameKey: "addonBVitamins",
    cmsPackageId: "addon-bvitamins",
    image: blobMediaUrl("/images/addons/multi-vial.png"),
    durationKey: "15 min",
    radiationKey: "piNoRadiation",
    priceKey: "addonBVitamins",
    descKey: "infoBVitaminsDesc",
    benefitKeys: ["piAddonBVitaminsBenefit1"],
    audienceKeys: [
      "piAddonBVitaminsAudience1",
      "piAddonBVitaminsAudience2",
      "piAddonBVitaminsAudience3",
    ],
    whatYouGetKeys: [
      "piInsightsByPhysicians",
      "piDetailedReport",
      "piPersonalActionPlan",
      "piAddTestsMriAnytime",
    ],
  },
  addonOmega: {
    availability: NOT_SOLD_IN_AUSTRIA,
    nameKey: "addonOmega",
    cmsPackageId: "addon-omega",
    image: blobMediaUrl("/images/addons/multi-vial.png"),
    durationKey: "15 min",
    radiationKey: "piNoRadiation",
    priceKey: "addonOmega",
    descKey: "infoOmegaDesc",
    benefitKeys: ["piAddonOmegaBenefit1", "piAddonOmegaBenefit2"],
    audienceKeys: ["piAddonOmegaAudience1", "piAddonOmegaAudience2"],
    whatYouGetKeys: [
      "piInsightsByPhysicians",
      "piDetailedReport",
      "piPersonalActionPlan",
      "piAddTestsMriAnytime",
    ],
  },
  addonHormoneWomen: {
    availability: NOT_SOLD_IN_AUSTRIA,
    nameKey: "addonHormoneWomen",
    cmsPackageId: "addon-hormones-f",
    image: blobMediaUrl("/images/addons/multi-vial-purple.png"),
    durationKey: "15 min",
    radiationKey: "piNoRadiation",
    priceKey: "addonHormoneWomen",
    descKey: "infoHormoneWomenDesc",
    benefitKeys: ["piAddonHormoneWomenBenefit1", "piAddonHormoneWomenBenefit2"],
    audienceKeys: [
      "piAddonHormoneWomenAudience1",
      "piAddonHormoneWomenAudience2",
    ],
    whatYouGetKeys: [
      "piInsightsByPhysicians",
      "piDetailedReport",
      "piPersonalActionPlan",
      "piAddTestsMriAnytime",
    ],
  },
  addonHormoneMen: {
    availability: NOT_SOLD_IN_AUSTRIA,
    nameKey: "addonHormoneMen",
    cmsPackageId: "addon-hormones-m",
    image: blobMediaUrl("/images/addons/multi-vial-purple.png"),
    durationKey: "15 min",
    radiationKey: "piNoRadiation",
    priceKey: "addonHormoneMen",
    descKey: "infoHormoneMenDesc",
    benefitKeys: ["piAddonHormoneMenBenefit1", "piAddonHormoneMenBenefit2"],
    audienceKeys: ["piAddonHormoneMenAudience1", "piAddonHormoneMenAudience2"],
    whatYouGetKeys: [
      "piInsightsByPhysicians",
      "piDetailedReport",
      "piPersonalActionPlan",
      "piAddTestsMriAnytime",
    ],
  },
  addonLpa: {
    availability: NOT_SOLD_IN_AUSTRIA,
    nameKey: "addonLpa",
    image: blobMediaUrl("/images/addons/single-vial.png"),
    radiationKey: "piNoRadiation",
    contrastDyeKey: "piNoContrastDye",
    price: "CHF 29",
    descKey: "infoLpaDesc",
    benefitKeys: ["piAddonLpaBenefit1", "piAddonLpaBenefit2"],
    audienceKeys: ["piAddonLpaAudience1", "piAddonLpaAudience2"],
    whatYouGetKeys: [
      "piInsightsByPhysicians",
      "piDetailedReport",
      "piPersonalActionPlan",
      "piAddTestsMriAnytime",
    ],
  },
  addonPsa: {
    // PSA carries two hides, so it spells its own map rather than sharing
    // NOT_SOLD_IN_AUSTRIA: not offered in NL, and not sold in Austria.
    availability: { "nl-nl": false, "nl-en": false, "at-de": false },
    nameKey: "addonPsa",
    image: blobMediaUrl("/images/addons/single-vial.png"),
    radiationKey: "piNoRadiation",
    contrastDyeKey: "piNoContrastDye",
    price: "CHF 19",
    descKey: "infoPsaDesc",
    benefitKeys: ["piAddonPsaBenefit1", "piAddonPsaBenefit2"],
    audienceKeys: ["piAddonPsaAudience1", "piAddonPsaAudience2"],
    whatYouGetKeys: [
      "piInsightsByPhysicians",
      "piDetailedReport",
      "piPersonalActionPlan",
      "piAddTestsMriAnytime",
    ],
  },
  addonCortisol: {
    availability: NOT_SOLD_IN_AUSTRIA,
    nameKey: "addonCortisol",
    image: blobMediaUrl("/images/addons/single-vial.png"),
    radiationKey: "piNoRadiation",
    contrastDyeKey: "piNoContrastDye",
    price: "CHF 29",
    descKey: "infoCortisolDesc",
    benefitKeys: ["piAddonCortisolBenefit1", "piAddonCortisolBenefit2"],
    audienceKeys: ["piAddonCortisolAudience1", "piAddonCortisolAudience2"],
    whatYouGetKeys: [
      "piInsightsByPhysicians",
      "piDetailedReport",
      "piPersonalActionPlan",
      "piAddTestsMriAnytime",
    ],
  },
  addonCoeliac: {
    availability: CH_ONLY,
    nameKey: "addonCoeliac",
    cmsPackageId: "addon-coeliac",
    image: blobMediaUrl("/images/addons/single-vial.png"),
    durationKey: "15 min",
    radiationKey: "piNoRadiation",
    priceKey: "addonCoeliac",
    descKey: "infoCoeliacDesc",
    benefitKeys: ["piAddonCoeliacBenefit1", "piAddonCoeliacBenefit2"],
    audienceKeys: ["piAddonCoeliacAudience1", "piAddonCoeliacAudience2"],
    whatYouGetKeys: [
      "piInsightsByPhysicians",
      "piDetailedReport",
      "piPersonalActionPlan",
      "piAddTestsMriAnytime",
    ],
  },
  addonHeavyMetals: {
    availability: CH_ONLY,
    nameKey: "addonHeavyMetals",
    cmsPackageId: "addon-heavy-metals",
    image: blobMediaUrl("/images/addons/multi-vial.png"),
    durationKey: "15 min",
    radiationKey: "piNoRadiation",
    priceKey: "addonHeavyMetals",
    descKey: "infoHeavyMetalsDesc",
    benefitKeys: ["piAddonHeavyMetalsBenefit1", "piAddonHeavyMetalsBenefit2"],
    audienceKeys: [
      "piAddonHeavyMetalsAudience1",
      "piAddonHeavyMetalsAudience2",
    ],
    whatYouGetKeys: [
      "piInsightsByPhysicians",
      "piDetailedReport",
      "piPersonalActionPlan",
      "piAddTestsMriAnytime",
    ],
  },
  addonTraceElements: {
    availability: CH_ONLY,
    nameKey: "addonTraceElements",
    cmsPackageId: "addon-trace-elements",
    image: blobMediaUrl("/images/addons/multi-vial.png"),
    durationKey: "15 min",
    radiationKey: "piNoRadiation",
    priceKey: "addonTraceElements",
    descKey: "infoTraceElementsDesc",
    benefitKeys: [
      "piAddonTraceElementsBenefit1",
      "piAddonTraceElementsBenefit2",
    ],
    audienceKeys: [
      "piAddonTraceElementsAudience1",
      "piAddonTraceElementsAudience2",
    ],
    whatYouGetKeys: [
      "piInsightsByPhysicians",
      "piDetailedReport",
      "piPersonalActionPlan",
      "piAddTestsMriAnytime",
    ],
  },
}
